import React, {useMemo} from "react";
import {Box, Button} from "@material-ui/core";
import {useOrganiser, useOrganiserShows} from "hooks";
import {Currency} from "@evennt-tech/api-v2-core";
import {currencySymbols} from "constraints/general";
import {useOrganiserStripeConnectAccounts} from "hooks/useStripeConnectAccounts";
import {useMaintenanceModeStore} from "store/general/maintenanceMode.store";
import {useImpersonationBannerStore} from "store/general/impersonationBanner.store";
import useStyles from "../../styles";
import {APP_BAR_HEIGHT} from "../../../AppBar";
import {getStripeRedirectUrl, STRIPE_CONNECT_BAR_HEIGHT} from "../../helpers";

const OrganiserBar = () => {
  const classes = useStyles();
  const [organiser] = useOrganiser();
  const [shows] = useOrganiserShows();

  const {
    panelHeight,
    shouldShowMaintenanceModeNotification,
  } = useMaintenanceModeStore();
  const bannerHeight = useImpersonationBannerStore(
    (state) => state.bannerHeight
  );

  const organiserCompanyId =
    organiser?.organiserCompanyToOrganisers?.[0]?.organiserCompanyId;

  // Shared cached query — same key as the layout connect-bar gate, so
  // react-query serves this from cache (no extra request).
  const {accounts} = useOrganiserStripeConnectAccounts(organiserCompanyId);

  const renderedCurrencies = useMemo<Currency[]>(() => {
    const existingCurrencies = new Set(
      accounts.map((acc) => acc.stripe?.currency)
    );
    return Object.values(Currency).filter(
      (currency) =>
        shows.some((show) => show.currency === currency) &&
        !existingCurrencies.has(currency)
    );
  }, [accounts, shows]);

  const redirectToStripeCreateAccountPage = async ({
    currency,
  }: {
    currency: Currency;
  }): Promise<void> => {
    try {
      const domain = window.location.origin;
      const redirectUrl = await getStripeRedirectUrl(currency, domain);

      if (redirectUrl) {
        window.location.replace(redirectUrl);
      }
    } catch (error) {
      console.error("Failed to get Stripe redirect URL", error);
    }
  };

  const ConnectButtons = () => (
    <Box>
      {renderedCurrencies.map((currency) => (
        <Button
          key={currency}
          onClick={() => redirectToStripeCreateAccountPage({currency})}
          className={classes.connectButton}
        >
          Connect {currencySymbols[currency]}
        </Button>
      ))}
    </Box>
  );

  return (
    <Box
      className={classes.bar}
      style={{
        height: STRIPE_CONNECT_BAR_HEIGHT,
        top:
          APP_BAR_HEIGHT +
          bannerHeight +
          (shouldShowMaintenanceModeNotification() ? panelHeight : 0),
      }}
    >
      <Box>Register your Stripe account and connect it to FFAIR.</Box>
      <ConnectButtons />
    </Box>
  );
};

export default OrganiserBar;
